import {ReactElement} from 'react'
import {Trans} from 'react-i18next'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'

import messages from '../../../../../assets/messages'
import {formatTauroDrawdownPercent} from '../../../../../common/utils/accounts'
import {formatTauroReloadFigure, getTauroReloadPreview, TauroReloadTerms} from '../../utils'

const TAURO_ORANGE = '#F57C00'

const useStyles = makeStyles(() => ({
  note: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 8,
    border: `1px solid ${TAURO_ORANGE}`,
    backgroundColor: 'rgba(245, 124, 0, 0.08)',
  },
  title: {
    fontWeight: 600,
    color: TAURO_ORANGE,
    marginBottom: 4,
  },
  figures: {
    margin: '8px 0 0',
    paddingInlineStart: 20,
  },
}))

type TauroReloadNoteProps = {
  /** Intro: terms of the next round under the account picker. Summary: figures at the payment method step. */
  variant: 'intro' | 'summary'
  login: string | number | undefined
  terms: TauroReloadTerms
  /** The amount the client entered, in the account currency. */
  amount: number
  locale: string
}

/**
 * PT-7878: the note on the Deposit tab for a liquidated Factorised Account that can reload. The
 * terms stay as fixed at the first funding; the factor applies once, to the new deposit.
 */
function TauroReloadNote(props: TauroReloadNoteProps): ReactElement {
  const {variant, login, terms, amount, locale} = props
  const classes = useStyles()
  const currency = terms.currency ?? ''
  const format = (value: number | null) => formatTauroReloadFigure(value ?? 0, locale)

  if (variant === 'summary') {
    const preview = getTauroReloadPreview(amount, terms)
    return (
      <div className={classes.note} data-testid="tauro-reload-summary">
        <ul className={classes.figures}>
          <li>
            <Trans
              {...messages.tauroReloadSummaryDeposit}
              values={{deposit: format(preview.deposit), currency}}
            />
          </li>
          <li>
            <Trans
              {...messages.tauroReloadSummaryStart}
              values={{start: format(preview.startingBalance), currency}}
            />
          </li>
          <li>
            <Trans
              {...messages.tauroReloadSummaryFloor}
              values={{floor: format(preview.floor), currency}}
            />
          </li>
        </ul>
      </div>
    )
  }

  return (
    <div className={classes.note} data-testid="tauro-reload-note">
      <Typography variant="body1" className={classes.title}>
        <Trans {...messages.tauroReloadTitle} values={{login}} />
      </Typography>
      <Typography variant="body2">
        <Trans
          {...messages.tauroReloadIntro}
          values={{
            round: terms.nextRound,
            factor: terms.factor,
            drawdown: formatTauroDrawdownPercent(terms.drawdownPercent),
            splitClient: terms.splitClient,
            splitTauro: terms.splitTauro,
            leverage: terms.leverage ?? '',
          }}
        />
      </Typography>
      <ul className={classes.figures}>
        {terms.minDeposit != null && terms.maxDeposit != null && (
          <li>
            <Trans
              {...messages.tauroReloadDepositRange}
              values={{min: format(terms.minDeposit), max: format(terms.maxDeposit), currency}}
            />
          </li>
        )}
        <li>
          <Trans {...messages.tauroReloadStartingRule} values={{factor: terms.factor}} />
        </li>
      </ul>
    </div>
  )
}

export {TauroReloadNote}
